import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Product, RawItem } from "@/types/models";
import type { StorePricingMode } from "@/lib/pricing";
import { brlPriceRegex } from "@/lib/price";
import { ProductPricingSection } from "./ProductPricingSection";
import type { ProductBomLine } from "./ProductBillOfMaterialsEditor";
import {
  productToBomLines,
  productToPricingFormValues,
  toProductPricingInput,
  type ProductPricingFormValues,
} from "../lib/productPricingInput";

export interface ProductPricingDialogProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rawItems: RawItem[];
  storeMarkupPercent?: number | null;
  storeTargetNetMarginPercent?: number | null;
  storePricingMode?: StorePricingMode | null;
  storeTaxPercent?: number | null;
  saving?: boolean;
  onSave: (
    input: ReturnType<typeof toProductPricingInput>,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => void;
}

export function ProductPricingDialog({
  product,
  open,
  onOpenChange,
  rawItems,
  storeMarkupPercent,
  storeTargetNetMarginPercent,
  storePricingMode,
  storeTaxPercent,
  saving = false,
  onSave,
}: ProductPricingDialogProps) {
  const [bomLines, setBomLines] = useState<ProductBomLine[]>([]);
  const [values, setValues] = useState<ProductPricingFormValues>({
    cost: "",
    staticCost: "",
    price: "",
    markupPercent: "",
    targetNetMarginPercent: "",
    effectiveTaxPercent: "",
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!product || !open) return;

    setBomLines(productToBomLines(product));
    setValues(productToPricingFormValues(product));
    setValidationError(null);
    setActionError(null);
  }, [product, open]);

  const handleSave = () => {
    if (!product) return;

    if (!values.price.trim() || !brlPriceRegex.test(values.price.trim())) {
      setValidationError("Preço inválido. Use formato 199,90");
      return;
    }

    setValidationError(null);
    setActionError(null);

    onSave(toProductPricingInput(product.id, values, bomLines), {
      onCompleted: () => onOpenChange(false),
      onError: (message) => setActionError(message),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-3xl flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:w-full"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="shrink-0 space-y-4 px-4 pt-4 sm:px-6 sm:pt-6">
          <DialogHeader>
            <DialogTitle>
              {product ? `Precificar · ${product.name}` : "Precificar produto"}
            </DialogTitle>
            <DialogDescription>
              {product
                ? "Defina o preço de venda com base nos custos e parâmetros abaixo."
                : "Selecione um produto para precificar."}
            </DialogDescription>
          </DialogHeader>

          {actionError && (
            <Alert variant="destructive">
              <AlertTitle>Erro</AlertTitle>
              <AlertDescription>{actionError}</AlertDescription>
            </Alert>
          )}

          {validationError && (
            <Alert variant="destructive">
              <AlertTitle>Validação</AlertTitle>
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
        </div>

        {product && (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <ProductPricingSection
              rawItems={rawItems}
              bomLines={bomLines}
              onBomLinesChange={setBomLines}
              values={values}
              onChange={setValues}
              storeMarkupPercent={storeMarkupPercent}
              storeTargetNetMarginPercent={storeTargetNetMarginPercent}
              storePricingMode={storePricingMode}
              storeTaxPercent={storeTaxPercent}
            />
          </div>
        )}

        <DialogFooter className="shrink-0 flex-col gap-2 border-t border-primary-200 bg-background px-4 py-4 sm:flex-row sm:px-6 dark:border-primary-800">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving || !product} onClick={handleSave}>
            {saving ? "Salvando…" : "Salvar precificação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
